import type { DisciplineFilesIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getDisciplineFilesFilterParams } from '../lib/discipline-files-query'
import { useDisciplineFilesStore } from '../model/files/store'

interface DisciplineFilesApiOptions {
  disciplineId: string | number
}

/** Filters and pages change faster than the list answers, so only the latest request may land. */
let activeRequest: AbortController | null = null

export const abortDisciplineFilesApi = (): void => {
  activeRequest?.abort()
  activeRequest = null
  useDisciplineFilesStore().setLoading(false)
}

/** `GET /admin-api/disciplines/{discipline}/files` — the sent packs plus the PLANNED tasks for the selects. */
export const useDisciplineFilesApi = async (options: DisciplineFilesApiOptions): Promise<DisciplineFilesIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useDisciplineFilesStore()

  const url = useAdminRoutes('admin.disciplines.api.files', pick({
    discipline_id: options.disciplineId,
    ...getDisciplineFilesFilterParams(store.getFilters),
    per_page: store.getPerPage,
    page: store.getCurrentPage,
  }))

  activeRequest?.abort()
  const request = new AbortController()
  activeRequest = request

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: request.signal })

    if (request.signal.aborted) return null

    const indexData: DisciplineFilesIndexData | null = data.data || null

    if (indexData) {
      store.setPacks(indexData.packs)
      store.setTasks(indexData.tasks)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch (err: unknown) {
    if (request.signal.aborted) return null

    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    if (activeRequest === request) {
      activeRequest = null
      store.setLoading(false)
    }
  }
}
